import { Navigate, Route, Routes } from 'react-router';
import { observer } from 'mobx-react';
import { paths, routes } from './utils/constants/routes';

// components
import Header from './components/navigation/Header';
import Footer from './components/navigation/Footer';
import MobileNavigation from './components/navigation/MobileNavigation';
import CookieBanner from './components/banners/CookieBanner';

// auth pages
import Signup from './pages/auth/Signup';
import ForgottenPassword from './pages/auth/ForgottenPassword';
import MfaSetup from './pages/auth/MfaSetup';
import MfaRecovery from './pages/auth/MfaRecovery';
import Profile from './pages/auth/Profile';
import VerificationProgress from './pages/auth/VerificationProgress';

// static pages
// import CookiePolicy from './pages/static/CookiePolicy';
// import Imprint from './pages/static/Imprint';
// import PrivacyPolicy from './pages/static/PrivacyPolicy';
// import TermsAndConditions from './pages/static/TermsAndConditions';

// role non-dependent pages
import Home from './pages/Home';
import CompanyProfile from './pages/CompanyProfile';
import CreateCompanyProfile from './pages/CompanyProfile/CreateCompanyProfile';
import Outreach from './pages/Outreach';

// agent pages
import Agent from './pages/Agent';
import AgentChatPage from './pages/Agent/chat/AgentChatPage';
import AgentConversationListView from './pages/Agent/conversations/AgentConversationListView';
import AgentProvider from './components/agent/AgentProvider';
import { ConfirmDialogProvider } from './components/dialogs/ConfirmDialog';
import { SessionLimitModalProvider } from './components/dialogs/SessionLimitModalProvider';
import SearchResults from './pages/search/SearchResults';
import Projects from './pages/Projects';
import Team from './pages/Team';

// investor pages
import Funds from './pages/Funds';
import AllInvestments from './pages/AllInvestments';
import Profiler from './pages/Profiler';
import ProfilerProjects from './pages/ProfilerProjects';
import ProfilerTeam from './pages/ProfilerTeam';
import ProfilerWatchList from './pages/ProfilerWatchList';
import CompanyDetailsPage from './pages/CompanyDetails';
import IndividualDetailsPage from './pages/IndividualDetails';

// admin pages
import AdminDashboard from './pages/AdminDashboard';

// hooks
import { useStore } from './store/store';
import useAppRouteGuard from './hooks/useAppRouteGuard';
import useAppSessionGuard from './hooks/useAppSessionGuard';
import useAppWrapClass from './hooks/useAppWrapClass';
import useAppLocationHistory from './hooks/useAppLocationHistory';
import useAppDemoCounterRefresh from './hooks/useAppDemoCounterRefresh';
import useAppAuthGuard from './hooks/useAppAuthGuard';
import useAppScrollEffect from './hooks/useAppScrollEffect';
import useElementHeightCssVar from './hooks/useElementHeightCssvar';
import useAppAnalytics from './hooks/useAppAnalytics';
import useResponsivePrivacyButton from './hooks/useResponsivePrivacyButton';
import useTabSafeTimers from './hooks/useTabSafeTimers';

const App = observer(() => {
  const { authStore } = useStore();
  const guard = useAppRouteGuard();
  const appClassName = useAppWrapClass();
  useTabSafeTimers();
  useAppSessionGuard();
  useAppLocationHistory();
  useAppDemoCounterRefresh();
  useAppAuthGuard();
  useAppScrollEffect();
  useAppAnalytics();
  useElementHeightCssVar('#root header', '--header-height');
  useElementHeightCssVar('#root footer', '--footer-height');
  useResponsivePrivacyButton();

  // NB: the Providers wrap BOTH the guard branch and the main app so the
  // SessionLimitModalProvider (and the SessionClaimController it mounts) stay
  // mounted across route-guard transitions — e.g. the AppLoader shown on every
  // fresh login while account details load. Mounting them only in the main
  // branch would remount the controller on each transition, re-fire the login
  // claim, and misroute a displacement to the take-over modal instead of the
  // designed force-logout (TD-2239).
  return (
    <ConfirmDialogProvider>
      <SessionLimitModalProvider>
        {guard || (
        <div className={appClassName}>
        <CookieBanner />
        <Header />
        {/* ✅ ULTIMATE FIX: AgentProvider at TOP LEVEL - survives ALL navigation */}
        <AgentProvider>
          <Routes>
            {/* role non-dependent pages start */}
            {[paths.HOME, paths.HOME_FULL].map((path) => (
              <Route key="home-route" path={path} element={<Home />} />
            ))}
            <Route path={paths.COMPANY_PROFILE} element={<CompanyProfile />} />
            <Route path={paths.CREATE_COMPANY_PROFILE} element={<CreateCompanyProfile />} />
            <Route path={paths.OUTREACH} element={<Outreach />} />
            {/* role non-dependent pages end */}

            {/* auth pages start */}
            <Route path={paths.SIGNUP} element={<Signup />} />
            <Route path={paths.FORGOTTEN_PASSWORD} element={<ForgottenPassword />} />
            <Route path={paths.MFA_SETUP} element={<MfaSetup />} />
            <Route path={paths.MFA_RECOVERY} element={<MfaRecovery />} />
            <Route path={paths.USER_VERIF_PROGRESS} element={<VerificationProgress />} />
            <Route path={routes.USER_PROFILE_PATH} element={<Profile />} />
            {/* auth pages end */}

            {/* agent pages start - Nested routes with top-level provider */}
            <Route
              path={`${paths.AGENT}/*`}
              element={
                <Routes>
                  <Route index element={<Agent />} />
                  <Route path="chat/:chatId" element={<AgentChatPage />} />
                  <Route path="conversations" element={<AgentConversationListView />} />
                  <Route path="favorites" element={<AgentConversationListView />} />
                  <Route path="archived" element={<AgentConversationListView />} />
                </Routes>
              }
            />
            <Route path={paths.SEARCH_RESULTS} element={<SearchResults />} />
            <Route path={paths.PROJECTS} element={<Projects />} />
            <Route path={paths.TEAM} element={<Team />} />
            {/* agent pages end */}

            {/* investor pages start */}
            {[routes.FUNDS_PATH, routes.PORTFOLIO_COMPANIES_PATH, routes.INVEST_PROFILES_PATH].map(
              (path) => (
                <Route key="funds-portfolios-profiles-route" path={path} element={<Funds />} />
              )
            )}
            <Route path={paths.ALL_INVESTMENTS} element={<AllInvestments />} />
            <Route path={paths.PROFILER} element={<Profiler />} />
            <Route path={paths.PROFILER_PROJECTS} element={<ProfilerProjects />} />
            <Route path={paths.PROFILER_TEAM} element={<ProfilerTeam />} />
            <Route path={paths.PROFILER_WATCH_LIST} element={<ProfilerWatchList />} />
            <Route path={routes.PROFILER_DETAILS_PATH} element={<CompanyDetailsPage />} />
            <Route path={routes.INDIVIDUAL_DETAILS_PATH} element={<IndividualDetailsPage />} />
            {/* investor pages end */}

            {/* admin pages start */}
            <Route path={paths.ADMIN_DASHBOARD} element={<AdminDashboard />} />
            {/* admin pages end */}

            {/* static pages */}
            {/* <Route path={paths.COOKIE_POLICY} element={<CookiePolicy />} />
        <Route path={paths.IMPRINT} element={<Imprint />} />
        <Route path={paths.PRIVACY_POLICY} element={<PrivacyPolicy />} />
        <Route path={paths.TERMS_N_CONDITIONS} element={<TermsAndConditions />} /> */}
            {/* end of static pages */}

            {/* fallback */}
            <Route
              path="*"
              element={<Navigate to={authStore.user ? paths.HOME_FULL : paths.HOME} replace />}
            />
            {/* fallback */}
          </Routes>
        </AgentProvider>
        <MobileNavigation />
        <Footer />
        </div>
        )}
      </SessionLimitModalProvider>
    </ConfirmDialogProvider>
  );
});

export default App;
